import { Analytics as TFAnalytics } from "@e4s-developers/tf-analytics";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import { useRouter } from "../hooks/useNavigation";
import { manageThaId, updateLastActivity } from "./tracking";

const AnalyticsContext = createContext();

export function AnalyticsProvider({
  children,
  env,
  userId,
  tenantId,
  sessionId,
}) {
  const isInitializedRef = useRef(false);
  const router = useRouter();
  const sentView = useRef(null);
  const pageStartTime = useRef(null);

  // Initialize analytics
  useEffect(() => {
    if (typeof window !== "undefined" && !isInitializedRef.current) {
      if (userId) {
        const setEnv = env === "dev" || env === "development" ? "dev" : "prod";
        TFAnalytics.initialize(setEnv, userId, null, null, tenantId, sessionId);
        isInitializedRef.current = true;
      }
    }
  }, [env, userId, tenantId, sessionId]);

  // Handle TalentHub account tracking - fire once on initial load
  useEffect(() => {
    if (typeof window !== "undefined") {
      const thaId = manageThaId();
      if (thaId) {
        updateLastActivity(thaId);
      }
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  const sendEvent = useCallback(
    (params) => {
      try {
        if (userId) {
          TFAnalytics.event(params);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [userId]
  );

  const sendTrackingEvent = useCallback(
    (name, label = null, data = {}) => {
      try {
        if (userId) {
          TFAnalytics.event({
            category: "CAREERS_SITE_ENGAGEMENT",
            name,
            label,
            data,
          });
          console.log("Tracking event sent", name, label, data);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [userId]
  );

  // Track page views, route changes, and page exits
  useEffect(() => {
    const handleRouteChangeStart = () => {
      sentView.current = null;

      const timeOnPage = pageStartTime.current
        ? Date.now() - pageStartTime.current
        : 0;

      if (!router.asPath.startsWith("/job")) {
        sendTrackingEvent("PAGE_EXIT", null, { timeOnPage });
      } else {
        sendTrackingEvent("VACANCY_EXIT", router.asPath.split("/").pop(), {
          timeOnPage,
        });
      }

      pageStartTime.current = null;
    };

    const handleRouteChangeComplete = (url) => {
      if (sentView.current === url) return;
      sentView.current = url;
      pageStartTime.current = Date.now();

      if (!url.startsWith("/job")) {
        sendTrackingEvent("PAGE_VIEW", null, { url });
      } else {
        const vacancyId = url.split("/").pop();
        sendTrackingEvent("VACANCY_VIEW", vacancyId);
      }
    };

    const handleBeforeUnload = () => {
      const timeOnPage = pageStartTime.current
        ? Date.now() - pageStartTime.current
        : 0;

      if (!router.asPath.startsWith("/job")) {
        sendTrackingEvent("PAGE_EXIT", null, { timeOnPage });
      } else {
        sendTrackingEvent("VACANCY_EXIT", router.asPath.split("/").pop(), {
          timeOnPage,
        });
      }
    };

    // Track initial page view
    if (typeof window !== "undefined") {
      const initialUrl = router.asPath;
      if (!sentView.current) {
        sentView.current = initialUrl;
        pageStartTime.current = Date.now();
        if (!initialUrl.startsWith("/job")) {
          sendTrackingEvent("PAGE_VIEW", null, { url: initialUrl });
        } else {
          const vacancyId = initialUrl.split("/").pop();
          sendTrackingEvent("VACANCY_VIEW", vacancyId);
        }
      }
    }

    // Attach all event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    if (router.events) {
      router.events.on("routeChangeStart", handleRouteChangeStart);
      router.events.on("routeChangeComplete", handleRouteChangeComplete);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (router.events) {
        router.events.off("routeChangeStart", handleRouteChangeStart);
        router.events.off("routeChangeComplete", handleRouteChangeComplete);
      }
    };
  }, [router, sendTrackingEvent]);

  const value = useMemo(
    () => ({
      sendEvent,
      sendTrackingEvent,
    }),
    [sendEvent, sendTrackingEvent]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

export function getUTMFromParams(queryString = "") {
  // Parse the query string into an object
  const params = new URLSearchParams(queryString);
  const queryObject = Array.from(params.entries()).reduce(
    (acc, [key, value]) => {
      return { ...acc, [key]: value };
    },
    {}
  );

  // Now use your existing logic on the queryObject
  const lowerCaseQuery = Object.entries(queryObject).reduce(
    (acc, [key, val]) => {
      const toCamel = (s) =>
        s.replace(/([-_][a-z])/gi, ($1) =>
          $1.toUpperCase().replace("-", "").replace("_", "")
        );
      let keyStr = toCamel(key.toLowerCase());

      if (keyStr === "jobboard") {
        keyStr = "utmSource";
      } else if (keyStr === "c") {
        keyStr = "utmCampaign";
      }

      return { ...acc, [keyStr]: val };
    },
    {}
  );

  const utm = {};

  if (lowerCaseQuery.utmSource) {
    utm.utm_source = lowerCaseQuery.utmSource;
  } else {
    utm.utm_campaign = "Direct";
  }

  if (lowerCaseQuery.utmMedium) {
    utm.utm_medium = lowerCaseQuery.utmMedium;
  }

  if (lowerCaseQuery.utmCampaign) {
    utm.utm_campaign = lowerCaseQuery.utmCampaign;
  }

  return Object.keys(utm).length ? utm : null;
}
